import { useId, useMemo, useState } from "react";
import { Input, Modal } from "@iq/models";
import { createPayment } from "../../services/paymentApi";
import "../../styles/Modal.css";
import "./CreatePayment.css";

function CreatePayment({ isOpen, onClose, clientId, client, onCreated }) {
	const formId = useId();
	const [form, setForm] = useState({
		paymentType: "one_time",
		description: "",
		amount: "",
		currency: "USD",
		interval: "monthly",
		recurringAmount: "",
		startDate: "",
		metadata: "",
	});
	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState("");
	const [createdLink, setCreatedLink] = useState("");
	const [copied, setCopied] = useState(false);

	const isSubscription = form.paymentType === "subscription";

	const actionButtons = useMemo(() => ([
		{
			key: "cancel-create-payment",
			text: "Cancel",
			color: "dark",
			onClick: handleClose,
			disabled: submitting,
		},
		{
			key: "submit-create-payment",
			text: submitting ? "Starting..." : "Start Payment",
			color: "accent",
			type: "submit",
			form: formId,
			disabled: submitting,
		},
	]), [formId, handleClose, submitting]);

	function updateField(field, value) {
		setForm((prev) => ({ ...prev, [field]: value }));
	}

	function resetForm() {
		setForm({
			paymentType: "one_time",
			description: "",
			amount: "",
			currency: "USD",
			interval: "monthly",
			recurringAmount: "",
			startDate: "",
			metadata: "",
		});
		setSubmitError("");
		setCreatedLink("");
		setCopied(false);
	}

	function dollarsToCents(amountValue) {
		return Math.round(Number(amountValue) * 100);
	}

	function toStripeInterval(value) {
		if (value === "annually") {
			return "year";
		}
		return "month";
	}

	function buildPayload() {
		const amount = Number(form.amount);
		if (!Number.isFinite(amount) || amount <= 0) {
			throw new Error("Amount must be greater than 0");
		}

		const description = form.description.trim();
		if (!description) {
			throw new Error("Payment description is required");
		}

		let parsedMetadata = {};
		if (form.metadata.trim()) {
			try {
				parsedMetadata = JSON.parse(form.metadata);
			} catch {
				throw new Error("Metadata must be valid JSON");
			}
		}

		const isSubscriptionPayment = form.paymentType === "subscription";
		const payload = {
			product_name: description,
			amount_cents: dollarsToCents(amount),
			currency: (form.currency || "USD").trim().toLowerCase(),
			mode: isSubscriptionPayment ? "subscription" : "payment",
			client_id: Number(clientId) || null,
			start_date: form.startDate || null,
			metadata: parsedMetadata,
		};

		if (isSubscriptionPayment) {
			const recurringAmount = Number(form.recurringAmount);
			if (!Number.isFinite(recurringAmount) || recurringAmount <= 0) {
				throw new Error("Recurring amount must be greater than 0 for subscriptions");
			}

			if (!form.interval) {
				throw new Error("Subscription interval is required");
			}

			return {
				...payload,
				amount_cents: dollarsToCents(recurringAmount),
				interval: toStripeInterval(form.interval),
				interval_count: 1,
			};
		}

		return payload;
	}

	async function handleSubmit(event) {
		event.preventDefault();
		setSubmitError("");
		setCreatedLink("");
		setCopied(false);
		setSubmitting(true);

		try {
			const payload = buildPayload();
			const created = await createPayment(payload);
			const linkUrl = String(created?.url || "").trim();
			if (!linkUrl) {
				throw new Error("Payment link was created but no URL was returned");
			}
			setCreatedLink(linkUrl);
			onCreated?.(created);
		} catch (error) {
			setSubmitError(error?.message || "Failed to start payment");
		} finally {
			setSubmitting(false);
		}
	}

	async function handleCopyLink() {
		if (!createdLink) {
			return;
		}

		try {
			await navigator.clipboard.writeText(createdLink);
			setCopied(true);
		} catch {
			setSubmitError("Unable to copy link. Please copy it manually.");
		}
	}

	function handleClose() {
		if (submitting) {
			return;
		}
		resetForm();
		onClose?.();
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			title="Create Payment"
			overlayClassName="modal-backdrop"
			contentClassName="modal-card"
			actionButtons={actionButtons}
			errorMessage={submitError}
			shrink={true}
		>
			<div className="payment-create-modal-content">
				<form id={formId} className="payment-create-form" onSubmit={handleSubmit}>


					<div className="payment-create-type-toggle" role="tablist" aria-label="Payment type">
						<button
							type="button"
							className={[
								"payment-create-toggle-btn",
								form.paymentType === "one_time" ? "payment-create-toggle-btn--active" : "",
							].filter(Boolean).join(" ")}
							onClick={() => updateField("paymentType", "one_time")}
							disabled={submitting}
						>
							One-Time
						</button>
						<button
							type="button"
							className={[
								"payment-create-toggle-btn",
								form.paymentType === "subscription" ? "payment-create-toggle-btn--active" : "",
							].filter(Boolean).join(" ")}
							onClick={() => updateField("paymentType", "subscription")}
							disabled={submitting}
						>
							Subscription
						</button>
					</div>

					<div className="payment-create-grid">
						<label className="payment-create-field">
							<span>Payment Amount</span>
							<Input
								type="number"
								name="amount"
								value={form.amount}
								onChange={(event) => updateField("amount", event.target.value)}
								placeholder="0.00"
								min="0"
								step="0.01"
								disabled={submitting}
								colorScheme="light"
							/>
						</label>

						<label className="payment-create-field">
							<span>Currency</span>
							<Input
								type="text"
								name="currency"
								value={form.currency}
								onChange={(event) => updateField("currency", event.target.value)}
								placeholder="USD"
								disabled={submitting}
								maxLength={5}
								colorScheme="light"
							/>
						</label>

						<label className="payment-create-field payment-create-field--full">
							<span>Payment Description</span>
							<Input
								type="textarea"
								name="description"
								value={form.description}
								onChange={(event) => updateField("description", event.target.value)}
								placeholder="What is this payment for?"
								disabled={submitting}
								rows={3}
								colorScheme="light"
							/>
						</label>

						{isSubscription ? (
							<>
								<label className="payment-create-field">
									<span>Subscription Interval</span>
									<select
										className="payment-create-select"
										value={form.interval}
										onChange={(event) => updateField("interval", event.target.value)}
										disabled={submitting}
									>
										<option value="monthly">Monthly</option>
										<option value="annually">Annually</option>
									</select>
								</label>

								<label className="payment-create-field">
									<span>Recurring Amount</span>
									<Input
										type="number"
										name="recurringAmount"
										value={form.recurringAmount}
										onChange={(event) => updateField("recurringAmount", event.target.value)}
										placeholder="0.00"
										min="0"
										step="0.01"
										disabled={submitting}
										colorScheme="light"
									/>
								</label>
							</>
						) : null}

						<label className="payment-create-field">
							<span>Start Date</span>
							<Input
								type="date"
								name="startDate"
								value={form.startDate}
								onChange={(event) => updateField("startDate", event.target.value)}
								disabled={submitting}
								colorScheme="light"
							/>
						</label>

						<label className="payment-create-field payment-create-field--full">
							<span>Metadata (JSON optional)</span>
							<Input
								type="textarea"
								name="metadata"
								value={form.metadata}
								onChange={(event) => updateField("metadata", event.target.value)}
								placeholder='{"source":"payment-ui"}'
								disabled={submitting}
								rows={3}
								colorScheme="light"
							/>
						</label>
					</div>
				</form>

				{createdLink ? (
					<div className="payment-create-success" role="status" aria-live="polite">
						<p className="payment-create-success-title">Payment link created</p>
						<a
							className="payment-create-link"
							href={createdLink}
							target="_blank"
							rel="noreferrer"
						>
							{createdLink}
						</a>
						<div className="payment-create-success-actions">
							<button
								type="button"
								className="payment-create-success-btn"
								onClick={() => window.open(createdLink, "_blank", "noopener,noreferrer")}
							>
								Open Link
							</button>
							<button
								type="button"
								className="payment-create-success-btn"
								onClick={handleCopyLink}
							>
								{copied ? "Copied" : "Copy Link"}
							</button>
						</div>
					</div>
				) : null}
			</div>
		</Modal>
	);
}

export default CreatePayment;
